import { Box, Chip } from "@mui/material";
import { Theme } from "klayowebshared";
import uniqueId from "lodash.uniqueid";
import React, { Component, SyntheticEvent } from "react";
import { v4 as uuidv4 } from "uuid";
import { Utils } from "../common/Utils";
import { withLocale, WithLocaleProps } from "../providers/LocaleProvider";
import DragIcon from "../resources/images/icons-drag.svg";
import RemoveIcon from "../resources/images/icons-remove.svg";
import { KLAYO_COLORS } from "../themes";
import { MultiFileUpload } from "./MultiFileUpload";
import { Placeholder } from "./Placeholder";
import { DocumentTable } from "./table/DocumentTable";

interface DragDropFileUploadProps extends WithLocaleProps {
  label?: string | null;
  accept?: string;
  allowedMimeTypes?: string[];
  allowedExtensions?: string[];
  maxFileSize?: number;
  errorMimeType?: string;
  errorFileSize?: string;
  allowedMimeTypesLabel?: string;
  disabled?: boolean;
  isShowInlineFiles?: boolean;
  theme?: typeof Theme;
  files?: File[];
  onChange?: (e: any, newFiles: File[]) => void;
}

interface DragDropFileUploadState {
  error: string | null;
  dragOver: boolean;
}

class DragDropFileUploadBase extends Component<DragDropFileUploadProps, DragDropFileUploadState> {
  static defaultProps = {
    allowedMimeTypes: ["*/*"],
    allowedExtensions: [],
    maxFileSize: 10240000,
    allowedMimeTypesLabel: "PDF, JPEG, PNG, DOC, DOCX, TXT, CSV, XLS, XLSX"
  };

  id: string;
  multiFileUploadRef: React.RefObject<MultiFileUpload | null>;
  dragDropSupport: boolean;

  constructor(props: DragDropFileUploadProps) {
    super(props);

    this.state = {
      error: null,
      dragOver: false
    };

    this.id = uniqueId("klayo_fileupload");
    this.multiFileUploadRef = React.createRef();

    this.dragDropSupport = "draggable" in document.createElement("div");
  }

  onDropZoneClick(e: React.MouseEvent) {
    this.multiFileUploadRef.current?.selectFile(e);
  }

  onDragEnter(e: React.DragEvent) {
    e.preventDefault();
    this.setState({ dragOver: true });
  }

  onDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    this.setState({ dragOver: false });
  }

  onDrop(e: React.DragEvent) {
    e.preventDefault();
    this.multiFileUploadRef.current?.dropFile(e);
    this.setState({ dragOver: false });
  }

  onChange(e: SyntheticEvent, newFiles: File[]) {
    const { onChange } = this.props;
    if (onChange) onChange(e, newFiles);
  }

  onRemoveFile(e: SyntheticEvent, key: string, file?: File) {
    this.multiFileUploadRef.current?.onRemoveFile(e, key, file);
  }

  onError(error: string | null) {
    this.setState({ error });
  }

  static isFilesEmpty(files: File[]) {
    return !files || Object.entries(files).filter(([key, file]) => file !== null).length === 0;
  }

  render() {
    const {
      label,
      accept,
      allowedMimeTypes,
      allowedExtensions,
      maxFileSize,
      errorMimeType,
      errorFileSize,
      allowedMimeTypesLabel,
      disabled,
      isShowInlineFiles,
      theme,
      files,
      translate
    } = this.props;
    const { error, dragOver } = this.state;

    const defaultErrorMessage =
      translate?.("messages.pleaseUploadValidFile") ?? "Please upload a valid file";
    const resolvedErrorMimeType = errorMimeType ?? defaultErrorMessage;
    const resolvedErrorFileSize = errorFileSize ?? defaultErrorMessage;

    const filesArray = files ? (Array.isArray(files) ? files : Object.values(files)) : [];
    const resetKey = filesArray.map((f: any) => f?.name).join("_") + filesArray.length;

    const fileEntries = Object.entries(files || {}).filter(([key, file]) => file !== null);

    return (
      <Box
        className={
          "klayo-upload" +
          (error ? " klayo-upload--error" : "") +
          (dragOver ? " klayo-upload--dragover" : "") +
          (disabled ? " klayo-upload--disabled" : "")
        }
      >
        {label && <div className='klayo-upload_label'>{label}</div>}
        <Box
          className='klayo-upload_dropzone'
          onDragOver={this.onDragOver.bind(this)}
          onDragEnter={this.onDragEnter.bind(this)}
          onDragLeave={this.onDragLeave.bind(this)}
          onDrop={this.onDrop.bind(this)}
          onClick={this.onDropZoneClick.bind(this)}
        >
          <Placeholder
            text={{
              primary: (
                <div>
                  <b>
                    {Utils.isTouchDevice()
                      ? (this.props.translate?.("fileUpload.tapToUpload") ?? "Tap")
                      : (this.props.translate?.("fileUpload.clickToUpload") ?? "Click")}{" "}
                    {this.props.translate?.("fileUpload.toUpload") ?? "to upload"}
                  </b>
                  {this.dragDropSupport
                    ? ` ${this.props.translate?.("fileUpload.orDragAndDrop") ?? "or drag and drop here"}`
                    : ""}
                </div>
              ),
              secondary: allowedMimeTypesLabel
            }}
            primaryTextSize='16px'
            secondaryTextSize='14px'
            spacing={2}
            icon={
              <DragIcon
                style={{
                  color: KLAYO_COLORS.NeonBlueNB200
                }}
              />
            }
            sx={{ pointerEvents: "none" }}
          />
        </Box>

        {error && <Box className='klayo-upload_error'>{error}</Box>}

        {!DragDropFileUploadBase.isFilesEmpty(files || []) && !isShowInlineFiles ? (
          <Box className='klayo-upload_chipcontainer'>
            {fileEntries.map(([key, file]) => {
              return (
                file && (
                  <Chip
                    key={key}
                    label={file.name}
                    deleteIcon={<RemoveIcon />}
                    onDelete={(e) => this.onRemoveFile(e, key)}
                  />
                )
              );
            })}
          </Box>
        ) : (
          <Box className='klayo-upload_chipcontainer'>
            {Object.values(Object.fromEntries(fileEntries)).length > 0 && (
              <DocumentTable
                theme={theme}
                showHeader={false}
                pagination={false}
                showFooter={false}
                hideFirstLastBorder={true}
                dense={true}
                onDeleteDocument={(_, row) => {
                  const file = row as unknown as File;
                  const fileKey = Object.entries(files || []).find(([key, f]) => f === file)?.[0];
                  if (fileKey) {
                    this.onRemoveFile(_, fileKey, file);
                  }
                }}
                paper={false}
                rows={
                  Object.values(Object.fromEntries(fileEntries)) as unknown as (File & {
                    [key: string]: unknown;
                  })[]
                }
                rowHeight={48}
                columns={[
                  {
                    type: DocumentTable.columns.name,
                    valueGetter: (_value, row) => row.name,
                    renderCell: (params) => <div>{params.row.name}</div>
                  },
                  { type: DocumentTable.columns.delete }
                ]}
                getRowId={(row) => row.name + uuidv4()}
              />
            )}
          </Box>
        )}

        <MultiFileUpload
          key={resetKey}
          id={this.id}
          ref={this.multiFileUploadRef}
          accept={accept}
          allowedMimeTypes={allowedMimeTypes}
          allowedExtensions={allowedExtensions}
          maxFileSize={maxFileSize}
          errorMimeType={resolvedErrorMimeType}
          errorFileSize={resolvedErrorFileSize}
          onChange={this.onChange.bind(this)}
          onError={this.onError.bind(this)}
          files={files}
          sx={{ display: "none" }}
        />
      </Box>
    );
  }
}

const DragDropFileUploadWithLocale = withLocale(DragDropFileUploadBase);

// Preserve static methods
(DragDropFileUploadWithLocale as any).isFilesEmpty = DragDropFileUploadBase.isFilesEmpty;

export const DragDropFileUpload =
  DragDropFileUploadWithLocale as typeof DragDropFileUploadWithLocale & {
    isFilesEmpty: typeof DragDropFileUploadBase.isFilesEmpty;
  };
